// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
/* eslint-disable react/prop-types */
import { useReactOidc } from '@axa-fr/react-oidc-context'
import { Box, FormField, SelectMultiple, Text } from 'grommet'
import React, { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { CircleInformation } from 'grommet-icons'

import {
  Button,
  CCSForm,
  ModalDialog,
  ModalHeader,
  Typography,
  Anchor,
  ButtonGroup,
  Dropdown
} from '../../../components'
import { getErrorMessage, post, get } from '../../../utils/api-utils'
import Markdown from '../../../commoncomponents/Markdown'

import FilterSubscription from './FilterSubscription'

const AddSubscription = ({
  onSetOpen,
  webhookInfo = {},
  subscriptionInfo = {},
  onToastNotification,
  setLoading
}) => {
  const { t } = useTranslation(['authz', 'common', 'automations'])
  const { oidcUser } = useReactOidc()
  const [isDisabled, setIsDisabled] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const [serviceManager, setServiceManager] = useState([])
  const [serviceManagerBackup, setServiceManagerBackup] = useState([])
  const [apiGroups, setApiGroups] = useState([])
  const [apiGroupsBackup, setApiGroupsBackup] = useState([])
  const [eventTypes, setEventTypes] = useState([])
  const [eventTypesBackup, setEventTypesBackup] = useState([])
  const [eventTypeErrorMessage, setEventTypeErrorMessage] = useState('')
  const [serviceErrorMessage, setServiceErrorMessage] = useState('')
  const [apiGroupErrorMessage, setApiGroupErrorMessage] = useState('')
  const { id } = useParams()
  const userCanAddEventsCount = 5 - subscriptionInfo.length

  const [isOpenSubscriptionLimitDialog, setIsOpenSubscriptionLimitDialog] =
    useState(false)
  const [formValues, setFormValues] = useState({
    eventType: subscriptionInfo?.eventType || [],
    serviceManager: undefined,
    apiGroup: ''
  })
  const subscribedEvents = subscriptionInfo.map((item) => item.eventType)
  const [showFilterButton, setShowFilterButton] = useState(false)
  const [isFilterVisible, setIsFilterVisible] = useState(false)
  const [selectedEvents, setSelectedEvents] = useState([])
  const [inputValues, setInputValues] = useState({})
  const fetchWebhook = useCallback(() => {
    get(`/events/v1beta1/service-managers`, {}, oidcUser?.access_token).then(
      (response) => {
        if (Array.isArray(response.data)) {
          const data = response?.data.map((item) => ({
            label: item?.name,
            value: item?.id
          }))
          setServiceManager(data)
          setServiceManagerBackup(data)
        }
      },
      (error) => {
        const fetchServiceManagerError = getErrorMessage(error, t)
        setErrorMessage(fetchServiceManagerError)
      }
    )
  }, [oidcUser?.access_token, t])

  useEffect(() => {
    fetchWebhook()
  }, [fetchWebhook])

  const onChangeServiceManager = (value) => {
    setServiceErrorMessage('')
    setFormValues({
      ...formValues,
      serviceManager: value,
      apiGroup: undefined,
      eventType: []
    })
    setInputValues({})
    setShowFilterButton(false)

    get(
      `/events/v1beta1/service-managers/${value}/api-groups`,
      {},
      oidcUser?.access_token
    ).then(
      (response) => {
        if (Array.isArray(response.data)) {
          const data = response.data.map((item) => ({
            label: item.name,
            value: item.id
          }))

          setApiGroups(data)
          setApiGroupsBackup(data)
        }
      },
      (error) => {
        const fetchAoiGroupError = getErrorMessage(error, t)
        setErrorMessage(fetchAoiGroupError)
      }
    )
  }

  const onChangeApiGroup = (value) => {
    setApiGroupErrorMessage('')
    setFormValues({
      ...formValues,
      apiGroup: value,
      eventType: []
    })
    setInputValues({})
    setShowFilterButton(false)

    get(
      `/events/v1beta1/api-groups/${value}/events`,
      {},
      oidcUser?.access_token
    ).then(
      (response) => {
        if (Array.isArray(response.data)) {
          const data = response.data.map((item) => ({
            title: item.name,
            subTitle: item.friendlyName,
            value: item.id
          }))
          setEventTypes(data)
          setEventTypesBackup(data)
        }
      },
      (error) => {
        const fetchEventTypeError = getErrorMessage(error, t)
        setErrorMessage(fetchEventTypeError)
      }
    )
  }

  const onSuccessHandler = (successTitle, successMessage) => {
    setLoading(false)
    onToastNotification({
      title: successTitle,
      notificationMessage: successMessage,
      severity: 'info'
    })
  }
  const onErrorHandler = () => {
    setLoading(false)
  }
  const addSubscription = async () => {
    let isValid = true
    const createSuccessMessage = (
      <Markdown>
        {t('automations:webhooks.add_subs_success_msg', {
          webhookName: webhookInfo?.name
        })}
      </Markdown>
    )

    if (!formValues?.serviceManager?.length) {
      setServiceErrorMessage(t('common:required_field_error'))
      isValid = false
    }

    if (!formValues?.apiGroup?.length && formValues?.serviceManager) {
      setApiGroupErrorMessage(t('common:required_field_error'))
      isValid = false
    }
    if (!formValues?.eventType?.length && formValues?.apiGroup) {
      setEventTypeErrorMessage(t('common:required_field_error'))
      isValid = false
    }

    if (isValid) {
      const requestBody = formValues?.eventType.map((eventTypeId) => {
        const eventType = eventTypes.find(
          (event) => event.value === eventTypeId
        )?.title
        return {
          eventType,
          serviceManager: formValues.serviceManager,
          webhook: {
            resourceUri: `/events/v1beta1/webhooks/${id}`
          },
          eventFilter: inputValues[eventTypeId] || ''
        }
      })
      try {
        setIsDisabled(true)
        await post(
          `/events/v1beta1/subscriptions`,
          requestBody,
          oidcUser?.access_token
        ).then(() => {
          setIsDisabled(false)
          onSuccessHandler('', createSuccessMessage)
          onSetOpen(false)
        })
      } catch (error) {
        setIsDisabled(false)
        setLoading(false)
        const createErrorMessage = getErrorMessage(error, t)
        setErrorMessage(createErrorMessage)
        onErrorHandler()
      }
    }
  }

  const handleClose = () => {
    setIsOpenSubscriptionLimitDialog(false)
    onSetOpen(false)
  }

  const onCloseDialog = () => {
    onSetOpen(false)
  }

  const onHandleChangeGroup = (value) => {
    const selectedEventIds = value.map((v) => v.value)
    const updatedInputValues = Object.keys(inputValues)
      .filter((key) => selectedEventIds.includes(key))
      .reduce((obj, key) => {
        obj[key] = inputValues[key]
        return obj
      }, {})

    setFormValues({
      ...formValues,
      eventType: selectedEventIds
    })
    setSelectedEvents(value)
    setShowFilterButton(value?.length > 0)
    setInputValues(updatedInputValues)
  }

  const onSearchServiceManager = (searchText) => {
    const escapedText = searchText.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&')
    const regexp = new RegExp(escapedText, 'i')
    if (searchText === '') {
      setServiceManager(serviceManagerBackup)
    } else {
      setServiceManager(
        serviceManagerBackup.filter((o) => regexp.test(o.label))
      )
    }
  }

  const onSearchApiGroup = (searchText) => {
    const escapedText = searchText.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&')
    const regexp = new RegExp(escapedText, 'i')
    if (searchText === '') {
      setApiGroups(apiGroupsBackup)
    } else {
      setApiGroups(apiGroupsBackup.filter((o) => regexp.test(o.label)))
    }
  }

  const onSearchEventType = (searchText) => {
    const escapedText = searchText.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&')
    const regexp = new RegExp(escapedText, 'i')
    if (searchText === '') {
      setEventTypes(eventTypesBackup)
    } else {
      setEventTypes(
        eventTypesBackup.filter(
          (o) => regexp.test(o.title) || regexp.test(o.subTitle)
        )
      )
    }
  }
  const filteredEventTypes = eventTypes
    .filter((option) => {
      return !subscribedEvents.includes(option.title)
    })
    .map((option) => {
      const isSelected = formValues.eventType.includes(option.value)
      const totalSelected =
        formValues.eventType.length + subscribedEvents.length
      const isOptionDisabled = !isSelected && totalSelected >= 5

      return {
        ...option,
        isDisabled: isOptionDisabled
      }
    })
  const OptionLabel = ({ subTitle, title }) => (
    <Box>
      <Text>{subTitle}</Text>
      <Text size="xsmall" color="gray">
        {title}
      </Text>
    </Box>
  )
  return (
    <>
      <ModalDialog
        header={
          <ModalHeader
            title={
              <Typography
                type="heading"
                level="2"
                testId="events-webhook-group-side-panel-title"
              >
                {t('automations:webhooks.create_subs_panel_title')}
              </Typography>
            }
            onClose={onCloseDialog}
          />
        }
        content={
          <Box margin={{ top: 'small' }} width={{ min: 'medium', max: 'full' }}>
            <CCSForm
              value={formValues}
              messages={{ required: t('common:this_is_required') }}
              errorMessage={
                errorMessage && (
                  <Box width="auto">
                    <Typography type="text" emphasis>
                      {errorMessage}
                    </Typography>
                  </Box>
                )
              }
              validate="blur"
              onChange={setFormValues}
              onSubmit={addSubscription}
              testId="events-webhook-group-modal-form"
            >
              <Box gap="small">
                {/* Event Type Input Field */}
                <Box gap="xxsmall">
                  <Typography
                    required
                    level="6"
                    type="text"
                    testId="events-webhook-description-sub-title"
                  >
                    {t('automations:webhooks.create_subs_description_subtitle')}
                    <Anchor
                      href="https://developer.greenlake.hpe.com/docs/greenlake/standards/draft/event/event_format/"
                      target="_blank"
                    >
                      {t('automations:webhooks.greenlake_events')}
                    </Anchor>{' '}
                    {t('automations:webhooks.create_subs_description_doc')}
                  </Typography>
                </Box>

                <Box margin={{ bottom: 'small' }}>
                  <Typography
                    level="4"
                    type="heading"
                    testId="events-webhook-dropdown-title"
                  >
                    {`${t('automations:webhooks.service_manager_heading')}*`}
                  </Typography>

                  <Typography
                    required
                    level="6"
                    type="text"
                    testId="events-webhook-dropdown-sub-title"
                    margin={{ bottom: 'xsmall' }}
                  >
                    {`${t('automations:webhooks.service_manager_sub_title')}`}
                  </Typography>

                  <FormField
                    htmlFor="dropdown_case-list"
                    error={serviceErrorMessage}
                    data-testid="service-manager-error"
                  >
                    <Dropdown
                      options={serviceManager}
                      onChangeDropdown={(value) => {
                        setServiceErrorMessage('')
                        setFormValues({
                          ...formValues,
                          serviceManager: value,
                          apiGroup: undefined,
                          eventType: []
                        })
                        onChangeServiceManager(value)
                        setServiceManager(serviceManagerBackup)
                      }}
                      defaultVal={formValues.serviceManager}
                      testId="service-manager-dropdown-field"
                      id="service-manager-dropdown-field"
                      name="service-manager-dropdown-field"
                      onSearch={onSearchServiceManager}
                      searchPlaceholder={t(
                        'automations:webhooks.search_placeholder'
                      )}
                      required
                      plain
                      data-testid="service-manager-dropdown-field"
                    />
                  </FormField>
                </Box>

                {formValues.serviceManager && (
                  <>
                    <Box>
                      <Typography
                        level="4"
                        type="heading"
                        testId="events-webhook-api-group-title"
                      >
                        {`${t('automations:webhooks.api_group_title')}*`}
                      </Typography>
                      <Typography
                        required
                        level="6"
                        type="text"
                        testId="events-webhook-api-group-sub-title"
                        margin={{ bottom: 'xsmall' }}
                      >
                        {`${t('automations:webhooks.api_group_sub_title')}`}
                      </Typography>
                    </Box>

                    <FormField
                      htmlFor="dropdown_case-list"
                      error={apiGroupErrorMessage}
                      data-testid="api-group-error"
                    >
                      <Dropdown
                        options={apiGroups}
                        onChangeDropdown={(value) => {
                          setApiGroupErrorMessage('')
                          setFormValues({
                            ...formValues,
                            apiGroup: value,
                            eventType: []
                          })
                          onChangeApiGroup(value)
                          setApiGroups(apiGroupsBackup)
                        }}
                        defaultVal={formValues.apiGroup}
                        testId="api-group-dropdown-field"
                        id="api-group-dropdown-field"
                        name="api-group-dropdown-field"
                        onSearch={onSearchApiGroup}
                        searchPlaceholder={t(
                          'automations:webhooks.search_placeholder'
                        )}
                        required
                        plain
                      />
                    </FormField>
                  </>
                )}

                {formValues.apiGroup && (
                  <>
                    <Typography
                      required
                      level="6"
                      type="text"
                      testId="events-webhook-api-group-note"
                      margin={{ bottom: 'small' }}
                    >
                      {subscriptionInfo.length > 0
                        ? t(
                            'automations:webhooks.select_event_restrict_label_msg',
                            {
                              remainEvent: userCanAddEventsCount,
                              existEvent: subscriptionInfo.length
                            }
                          )
                        : t('automations:webhooks.select_event_restrict_label')}
                    </Typography>

                    <Box margin={{ bottom: 'small' }}>
                      <Typography
                        level="4"
                        type="heading"
                        testId="events-webhook-desc-title"
                      >
                        {`${t('automations:webhooks.add_subs_event_type')}*`}
                      </Typography>
                      <Typography
                        required
                        level="6"
                        type="text"
                        testId="events-webhook-event-type-sub-title"
                      >
                        {t('automations:webhooks.add_subs_event_type_desc')}
                      </Typography>
                    </Box>

                    <Box>
                      <FormField
                        htmlFor="dropdown_case-list"
                        error={eventTypeErrorMessage}
                        data-testid="event-type-error"
                      >
                        <SelectMultiple
                          margin={{
                            bottom: 'xsmall',
                            left: 'xsmall',
                            top: 'xsmall'
                          }}
                          limit={userCanAddEventsCount}
                          options={filteredEventTypes}
                          placeholder={
                            formValues.eventType.length +
                              subscribedEvents.length >
                            0
                              ? `${
                                  formValues.eventType.length +
                                  subscribedEvents.length
                                } ${t('automations:webhooks.selected_label')}`
                              : t('common:select')
                          }
                          testId="events-webhook-event-type-form-field"
                          id="event-type-dropdown-field"
                          name="event-type-dropdown-field"
                          required
                          value={formValues.eventType}
                          onChange={({ value }) => {
                            setEventTypeErrorMessage('')
                            onHandleChangeGroup(value)
                            setEventTypes(eventTypesBackup)
                          }}
                          searchPlaceholder={t(
                            'automations:webhooks.search_placeholder'
                          )}
                          onSearch={onSearchEventType}
                          messages={{
                            selected: `${
                              formValues.eventType.length +
                              subscribedEvents.length
                            } ${t('automations:webhooks.selected_label')} (${t(
                              'automations:webhooks.max_of_5'
                            )})`,
                            selectedOfTotal: `${
                              formValues.eventType.length +
                              subscribedEvents.length
                            } ${t('automations:webhooks.selected_label')} (${t(
                              'automations:webhooks.max_of_5'
                            )})`
                          }}
                          valueLabel={
                            formValues.eventType.length +
                              subscribedEvents.length >
                            0
                              ? `${
                                  formValues.eventType.length +
                                  subscribedEvents.length
                                } ${t('automations:webhooks.selected_label')}`
                              : t('common:select')
                          }
                          labelKey={(option) => (
                            <OptionLabel
                              subTitle={option.subTitle}
                              title={option.title}
                            />
                          )}
                          valueKey="value"
                          dropHeight="medium"
                          dropProps={{
                            width: 'medium'
                          }}
                          data-testid="event-type-dropdown-field"
                        />
                      </FormField>
                    </Box>

                    <Box
                      background="background-contrast"
                      pad="small"
                      round="xsmall"
                      direction="column"
                      align="center"
                      gap="small"
                      margin={{ top: 'small' }}
                    >
                      <Box direction="row" align="flex-start" gap="small">
                        <CircleInformation size="large" />
                        <Typography
                          level="4"
                          type="text"
                          testId="subscription-notification"
                        >
                          {t('automations:webhooks.subscription_notification')}
                        </Typography>
                      </Box>
                    </Box>
                    <div>
                      {showFilterButton && (
                        <Box
                          margin={{ top: 'small' }}
                          direction="row"
                          align="center"
                          gap="small"
                        >
                          <Button
                            secondary
                            label={t('automations:webhooks.specify_filters')}
                            onClick={() => {
                              setIsFilterVisible(true)
                            }}
                            testId="specify-filter-button"
                          />
                          <Typography type="text" testId="filter-text">
                            {Object.keys(inputValues).length > 0
                              ? `${Object.keys(inputValues).length} ${t(
                                  'automations:webhooks.filters_specified'
                                )}`
                              : t('automations:webhooks.none_specified')}
                          </Typography>
                        </Box>
                      )}

                      {isFilterVisible && (
                        <FilterSubscription
                          onClose={() => setIsFilterVisible(false)}
                          selectedEvents={selectedEvents}
                          inputValues={inputValues}
                          setInputValues={setInputValues}
                        />
                      )}
                    </div>
                  </>
                )}

                <Box
                  direction="row"
                  justify="start"
                  gap="medium"
                  margin={{ top: 'small' }}
                  pad={{ top: 'medium' }}
                >
                  <ButtonGroup
                    buttonList={[
                      {
                        primary: true,
                        label: t('automations:webhooks.create_subs_btn_label'),
                        onClick: addSubscription,
                        disabled: isDisabled,
                        testId: 'add-subscription-submit-btn',
                        type: 'submit'
                      },
                      {
                        label: t('common:cancel'),
                        onClick: onCloseDialog,
                        testId: 'add-subscription-cancel-btn',
                        type: 'reset'
                      }
                    ]}
                    testId="add-subscription-button-group"
                  />
                </Box>
                <Box pad={{ bottom: 'medium' }} />
              </Box>
            </CCSForm>
          </Box>
        }
        onClose={onCloseDialog}
        position="right"
        height="100%"
        overflow="hidden"
        testId="edit-webhook-side-panel"
        width="440px"
      />
      {isOpenSubscriptionLimitDialog ? (
        <ModalDialog
          content={
            <Box>
              <Box direction="column">
                <h2 style={{ fontWeight: '500' }}>Register webhook</h2>
              </Box>
              <Box direction="column">
                {t('automations:webhooks.subs_register_limit_msg')}
              </Box>
            </Box>
          }
          footer={
            <Box style={{ display: 'flex', alignItems: 'end' }}>
              <Button
                primary
                label={t('common:close')}
                onClick={handleClose}
                testId="webhook-reach-limit-close-btn"
              />
            </Box>
          }
          onClose={handleClose}
          testId="webhook-reach-limit-modal"
          width="large"
        />
      ) : null}
    </>
  )
}

export default AddSubscription
